// Utilities for consistent date-time formatting in the browser timezone
// with a sensible fallback to Asia/Shanghai (+08:00) if the timezone
// cannot be resolved.

/**
 * Get the browser's current IANA timezone, falling back to Asia/Shanghai.
 */
export function getBrowserTimeZone() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    // Heuristic: some environments falsely report 'UTC' while the local
    // offset is not 0. In that case, prefer the E8 fallback as requested.
    const offset = new Date().getTimezoneOffset(); // minutes behind UTC, e.g. -480 for +08:00
    if (!tz || (tz === 'UTC' && offset !== 0)) return 'Asia/Shanghai';
    return tz;
  } catch (_) {
    return 'Asia/Shanghai';
  }
}

/**
 * Safely parse a date input into a Date object.
 * Accepts Date or string. Returns null if invalid.
 */
export function toDateSafe(input) {
  if (!input) return null;
  if (input instanceof Date) return isNaN(input.getTime()) ? null : input;
  const str = String(input).trim();
  // Normalize common variants: 'YYYY-MM-DD HH:mm:ss' -> 'YYYY-MM-DDTHH:mm:ss'
  let normalized = str.includes(' ') && !str.includes('T') ? str.replace(' ', 'T') : str;

  // Detect if string lacks timezone info (no 'Z' or ±HH:MM). Many backends
  // store UTC but omit the timezone. If so, treat as UTC explicitly.
  const hasTZ = /[zZ]|[+-]\d{2}:?\d{2}$/.test(normalized);
  if (!hasTZ && /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?/.test(normalized)) {
    normalized = normalized + 'Z';
  }

  const d = new Date(normalized);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Format a date in the browser's timezone; if unavailable, uses Asia/Shanghai.
 * Returns '—' for invalid/empty inputs.
 */
export function formatInBrowserTZ(input, options = undefined) {
  const d = toDateSafe(input);
  if (!d) return '—';
  const timeZone = getBrowserTimeZone();

  // Default full datetime format with 24-hour clock
  const base = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone
  };

  const fmtOptions = options && typeof options === 'object' ? { ...base, ...options, timeZone } : base;
  return new Intl.DateTimeFormat(undefined, fmtOptions).format(d).replaceAll('.', '/');
}
